import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LocalStorageService } from 'angular-2-local-storage';
import { SubscriptionService, SubscriptionTierOption } from '../../providers/subscription.service';

/**
 * View model for one tier card. Extends the plain
 * {@link SubscriptionTierOption} the backend returns with the
 * period-specific scheme ids/prices resolved separately (see
 * SubscriptionService#getSchemeIdsForTier) plus presentation flags.
 */
export interface TierCardViewModel {
  id: number;
  name: string;
  description: string;
  features: string[];
  isFree: boolean;
  isCurrentTier: boolean;
  isMostPopular: boolean;
  monthlyPrice: number | null;
  annualPrice: number | null;
  monthlySchemeId: number | null;
  annualSchemeId: number | null;
  schemeIdsLoaded: boolean;
}

/**
 * SG-177 Phase 6: the subscription overlay, now showing all 3
 * user-selectable tiers (Rookie, Commissioner, Pro) instead of the
 * original 2 (Rookie, Pro comparison table). This is the actual existing
 * "2-tier subscription overlay" referenced by the phase brief — it is
 * opened from lobby.component.ts, edit-profile.component.ts,
 * payment.component.ts and community-leaderboard.component.ts wherever a
 * Rookie user is prompted to upgrade. (`buy-subscription.component.ts` at
 * the brief's literally-named path is a separate, pre-existing,
 * unrelated legacy flow for NFL/IDP Guys virtual-coin value packs — not
 * an individual Rookie/Pro/Commissioner subscription overlay at all. See
 * phase-6-report.md for the full investigation; that component/route was
 * intentionally left untouched to avoid breaking that unrelated product
 * line.)
 *
 * Kept the class/selector/file name (`ProUpsellModalComponent`) unchanged
 * to avoid a wide, risky rename across its 4 existing call sites —
 * "extend, don't fork" per the phase brief.
 *
 * PLACEHOLDER: pending Marc — this is a clean, functional placeholder
 * layout (3-card grid) built ahead of the real design mockup, per
 * conventions.md.
 */
@Component({
  selector: 'app-pro-upsell-modal',
  templateUrl: './pro-upsell-modal.component.html',
  styleUrls: ['./pro-upsell-modal.component.css']
})
export class ProUpsellModalComponent implements OnInit {
  // Legacy inputs (kept for backward compatibility with existing call
  // sites, which pass either `data.pricing.dailyContestLimit` or a flat
  // `data.dailyContestLimit` — both handled below). No longer drive
  // pricing (that now comes live from the backend via SubscriptionService)
  // — only the "why you're seeing this" copy and the footer winner note.
  // null (rather than a default like 2) so the "you hit your daily limit"
  // banner only shows when a caller actually passes a limit (the 4
  // contest-limit call sites always do) — My Account's own "Upgrade"
  // entry point (Task 6.4) opens this same modal without that context and
  // relies on this being falsy to hide the banner via *ngIf in the template.
  dailyContestLimit: number | null = null;
  lastMonthTopWinner: number = 0;

  /** Optional: pass the user's current tier name ('Rookie'|'Commissioner'|'Pro')
   *  so the matching card can be marked "Current Plan" instead of showing a
   *  Select button (used by My Account's Upgrade dialog — Task 6.4). */
  currentTierName: string | null = null;

  /**
   * Optional "why am I seeing this" line for triggers that are NOT the
   * daily-contest limit (e.g. the Lobby's Create Private Contest button,
   * where the user lacks a subscription rather than having hit a cap).
   * Callers that pass dailyContestLimit keep their existing banner; this
   * is only rendered when they don't, so no existing call site changes.
   */
  contextMessage: string | null = null;

  selectedPlan: 'monthly' | 'annual' = 'monthly';
  /** True while ANY tier's purchase is in flight — disables every Select
   *  button so a second tier cannot be bought at the same time. */
  processing: boolean = false;

  /**
   * Which tier is actually being purchased. `processing` alone is not
   * enough: it is component-level, so binding the button label to it made
   * EVERY paid tier read "Processing…" as soon as one was clicked — a user
   * selecting Commissioner saw Pro claim to be processing too. The other
   * buttons still disable (correctly), they just keep their own label.
   */
  processingTierName: string | null = null;
  errorMessage: string = '';
  loadingTiers: boolean = true;
  tiers: TierCardViewModel[] = [];

  constructor(
    public dialogRef: MatDialogRef<ProUpsellModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private localStorageService: LocalStorageService,
    private subscriptionService: SubscriptionService
  ) {
    if (data && data.pricing && data.pricing.dailyContestLimit) {
      this.dailyContestLimit = data.pricing.dailyContestLimit;
    } else if (data && data.dailyContestLimit) {
      this.dailyContestLimit = data.dailyContestLimit;
    }
    if (data && data.topWinner) {
      this.lastMonthTopWinner = data.topWinner;
    }
    if (data && data.currentTierName) {
      this.currentTierName = data.currentTierName;
    }
    if (data && data.contextMessage) {
      this.contextMessage = data.contextMessage;
    }
  }

  ngOnInit(): void {
    this.loadTiers();
  }

  /**
   * Loads the 3 tiers (Task 6.1/6.3) from GET /api/subscription/options,
   * then resolves purchasable scheme ids for each paid tier
   * (Commissioner, Pro) exactly like the pre-Phase-6 Pro-only flow did,
   * just generalized to run for both paid tiers instead of just Pro.
   */
  loadTiers(): void {
    this.loadingTiers = true;
    this.errorMessage = '';
    this.subscriptionService.getSubscriptionOptions((options: SubscriptionTierOption[]) => {
      if (!options || options.length === 0) {
        this.tiers = [];
        this.loadingTiers = false;
        this.errorMessage = 'Unable to load subscription plans right now. Please try again later.';
        return;
      }
      this.tiers = options.map((o) => this._toViewModel(o));
      this.loadingTiers = false;
      this.tiers.filter((t) => !t.isFree).forEach((t) => this._resolveSchemeIds(t));
    });
  }

  private _toViewModel(option: SubscriptionTierOption): TierCardViewModel {
    return {
      id: option.id,
      name: option.name,
      description: option.description,
      features: option.features || [],
      isFree: option.name === 'Rookie',
      isCurrentTier: !!this.currentTierName && this.currentTierName === option.name,
      isMostPopular: option.name === 'Pro',
      monthlyPrice: option.price,
      annualPrice: null,
      monthlySchemeId: null,
      annualSchemeId: null,
      // Rookie is never purchased through this flow, so it never needs
      // scheme ids - treat it as already "loaded".
      schemeIdsLoaded: option.name === 'Rookie'
    };
  }

  private _resolveSchemeIds(tier: TierCardViewModel): void {
    this.subscriptionService.getSchemeIdsForTier(tier.name, (result) => {
      if (result.monthlyPrice !== null) {
        tier.monthlyPrice = result.monthlyPrice;
      }
      tier.annualPrice = result.annualPrice;
      tier.monthlySchemeId = result.monthlySchemeId;
      tier.annualSchemeId = result.annualSchemeId;
      tier.schemeIdsLoaded = true;
    });
  }

  selectPlan(plan: 'monthly' | 'annual'): void {
    this.selectedPlan = plan;
  }

  /**
   * Price to display for a tier under the currently-selected billing period,
   * or null when it is not known.
   *
   * This used to fall back to monthlyPrice when annualPrice was null, which
   * rendered the MONTHLY figure under a "/ yr" label — e.g. "$9.99 / yr" for
   * a plan that actually costs $89.99/yr. That window is real: annualPrice
   * starts null and is only filled in by the async _resolveSchemeIds()
   * lookup, and stays null permanently if that lookup fails or the tier has
   * no annual scheme seeded. Showing a wrong price is worse than showing
   * none, so return null and let the template say so.
   */
  priceForTier(tier: TierCardViewModel): number | null {
    if (tier.isFree) {
      return 0;
    }
    return this.selectedPlan === 'monthly' ? tier.monthlyPrice : tier.annualPrice;
  }

  /**
   * Task 6.3: purchase the selected tier via its scheme id. Works
   * identically for Commissioner and Pro — same call, same PayPal
   * redirect, same backend generic-by-scheme-id purchase plumbing (Phase
   * 2). Mirrors the pre-Phase-6 onUpgrade(): on success this does a full
   * page redirect (`window.location.href`), it does NOT call
   * `dialogRef.close()` — preserved exactly so the existing call sites'
   * `afterClosed()` handlers (which only ever observed 'dismissed'/
   * 'closed' in practice) see no behavior change for Pro.
   */
  selectTier(tier: TierCardViewModel): void {
    if (tier.isFree || tier.isCurrentTier || this.processing) {
      return;
    }
    const schemeId = this.selectedPlan === 'monthly' ? tier.monthlySchemeId : tier.annualSchemeId;
    if (!schemeId) {
      this.errorMessage = tier.name + ' plans are not yet available. Please try again later.';
      return;
    }

    this.processing = true;
    this.processingTierName = tier.name;
    this.errorMessage = '';
    const userId = this.localStorageService.get<any>('userId');

    this.subscriptionService.purchaseSubscription(schemeId, userId, (result) => {
      this.processing = false;
      this.processingTierName = null;
      if (result.success && result.approvalUrl) {
        // Store plan info for after PayPal redirect (same keys the
        // pre-existing Pro-only flow used, so payment.component.ts's
        // existing execute-plan logic needs no changes).
        localStorage.setItem('sg_pro_scheme_id', schemeId.toString());
        localStorage.setItem('sg_pro_plan_type', this.selectedPlan);
        // Tier identity for the post-PayPal success screen. The redirect below
        // is a full page load, so nothing survives except what is persisted
        // here. Price and features are stored alongside the name because
        // payment.component has no way to look them up again: it runs before
        // the tier list is fetched, and only has the scheme id.
        localStorage.setItem('sg_selected_tier_name', tier.name);
        const paidPrice = this.priceForTier(tier);
        if (paidPrice !== null) {
          localStorage.setItem('sg_selected_tier_price', String(paidPrice));
        }
        try {
          localStorage.setItem('sg_selected_tier_features', JSON.stringify(tier.features || []));
        } catch (e) {
          // Non-critical: the success screen falls back to generic copy.
        }
        window.location.href = result.approvalUrl;
      } else if (result.alreadySubscribed) {
        this.errorMessage = 'You already have an active ' + tier.name + ' subscription.';
      } else {
        this.errorMessage = result.errorMessage || 'Payment initiation failed. Please try again.';
      }
    });
  }

  /** True only for the tier whose purchase is actually in flight. */
  isProcessingTier(tier: TierCardViewModel): boolean {
    return this.processingTierName === tier.name;
  }

  onMaybeLater(): void {
    this.dialogRef.close({ action: 'dismissed' });
  }

  close(): void {
    this.dialogRef.close({ action: 'closed' });
  }
}
